import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useWishlist } from "../../context/WishlistContext";
import { useCart } from "../../context/CartContext";
import { useNotifications } from "../../context/NotificationsContext";
import { useMessages } from "../../context/MessagesContext";
import { updateFullName, uploadProfileImage, updateMarketingOptIn } from "../../api/userService";
import { fetchFollowedStoresCount, fetchStoreBellsCount } from "../../api/storeService";
import { getProducts } from "../../api/productService";
import ClientLoginModal from "./ClientLoginModal";
import LanguageSwitcher from "./LanguageSwitcher";
import "./clientTheme.css";
import "./ClientHeader.css";
import { useLanguage } from "../../context/LanguageContext";
import { localizedProductName } from "../../utils/localizedName";

function getErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === "string") return data;
  if (data?.message) return data.message;
  if (data?.title) return data.title;
  return fallback;
}

const SEARCH_SUGGESTIONS_MIN_LENGTH = 2;
const SEARCH_SUGGESTIONS_DEBOUNCE_MS = 300;
const SEARCH_SUGGESTIONS_TAKE = 6;

function getMainImage(product) {
  if (!product.images || product.images.length === 0) return null;
  return product.images.find((img) => img.isMain) || product.images[0];
}

function getInitials(fullName) {
  if (!fullName) return "?";
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const initials = parts.length === 1 ? parts[0][0] : parts[0][0] + parts[parts.length - 1][0];
  return initials.toUpperCase();
}

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function HeartIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z" />
    </svg>
  );
}

function CartIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="21" r="1.4" />
      <circle cx="18" cy="21" r="1.4" />
      <path d="M2.5 3h2.2l2.6 12.6a2 2 0 0 0 2 1.6h8.4a2 2 0 0 0 2-1.6L21.5 7H6" />
    </svg>
  );
}

function BellIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  );
}

function ReceiptIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2h12v20l-3-2-3 2-3-2-3 2Z" />
      <line x1="9" y1="7" x2="15" y2="7" />
      <line x1="9" y1="11" x2="15" y2="11" />
      <line x1="9" y1="15" x2="13" y2="15" />
    </svg>
  );
}

function MessageIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z" />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2Z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function MapPinIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
      <path d="M12 22s7-7.05 7-12a7 7 0 1 0-14 0c0 4.95 7 12 7 12z" />
      <circle cx="12" cy="10" r="2.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function StorefrontIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 9l1.5-5h15L21 9" />
      <path d="M3 9a2.5 2.5 0 0 0 5 0 2.5 2.5 0 0 0 5 0 2.5 2.5 0 0 0 5 0 2.5 2.5 0 0 0 5 0" />
      <path d="M5 9v10h14V9" />
      <path d="M9 19v-5h6v5" />
    </svg>
  );
}

function TagIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
      <path d="M20 12.5 12.5 20a1.5 1.5 0 0 1-2.12 0l-6.38-6.38a1.5 1.5 0 0 1 0-2.12L11.5 4H19a1 1 0 0 1 1 1v7.5z" />
      <circle cx="15.5" cy="8.5" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function MegaphoneIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m3 11 18-5v12L3 13v-2Z" />
      <path d="M11.6 16.8 13 21h-2.5l-2-5" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

export default function ClientHeader() {
  const { t } = useTranslation(["header", "common"]);
  const { language } = useLanguage();
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const searchRef = useRef(null);
  const suggestionsRequestIdRef = useRef(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [accountError, setAccountError] = useState("");
  const [followedStoresCount, setFollowedStoresCount] = useState(0);
  const [bellStoresCount, setBellStoresCount] = useState(0);
  const [savingMarketingOptIn, setSavingMarketingOptIn] = useState(false);
  const fileInputRef = useRef(null);
  const accountRef = useRef(null);
  const headerRef = useRef(null);
  const navigate = useNavigate();
  const { user, isAuthenticated, logout, updateUser, loginModalOpen, openLoginModal, closeLoginModal } = useAuth();
  const { itemCount: wishlistCount } = useWishlist();
  const { itemCount: cartCount } = useCart();
  const { unreadCount: notificationCount } = useNotifications();
  const { unreadCount: messageCount } = useMessages();

  useEffect(() => {
    if (!isAuthenticated) {
      setFollowedStoresCount(0);
      setBellStoresCount(0);
      return;
    }
    fetchFollowedStoresCount()
      .then(setFollowedStoresCount)
      .catch(() => {});
    fetchStoreBellsCount()
      .then(setBellStoresCount)
      .catch(() => {});
  }, [isAuthenticated]);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    function updateHeaderHeight() {
      document.documentElement.style.setProperty("--client-header-height", `${el.offsetHeight}px`);
    }
    updateHeaderHeight();
    // Older in-app browsers (e.g. Facebook's WebView) lack ResizeObserver;
    // an unguarded call would crash the whole page.
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateHeaderHeight);
      return () => window.removeEventListener("resize", updateHeaderHeight);
    }
    const observer = new ResizeObserver(updateHeaderHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(e) {
      if (accountRef.current && !accountRef.current.contains(e.target)) {
        closeAccountMenu();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuOpen]);

  useEffect(() => {
    if (!suggestionsOpen) return;
    function handleClickOutside(e) {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSuggestionsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [suggestionsOpen]);

  useEffect(() => {
    const trimmed = query.trim();
    setActiveSuggestionIndex(-1);

    if (trimmed.length < SEARCH_SUGGESTIONS_MIN_LENGTH) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      return;
    }

    const requestId = ++suggestionsRequestIdRef.current;
    setSuggestionsLoading(true);
    const timer = setTimeout(() => {
      getProducts({ search: trimmed, take: SEARCH_SUGGESTIONS_TAKE })
        .then((data) => {
          if (suggestionsRequestIdRef.current !== requestId) return;
          setSuggestions(data || []);
          setSuggestionsLoading(false);
        })
        .catch(() => {
          if (suggestionsRequestIdRef.current !== requestId) return;
          setSuggestions([]);
          setSuggestionsLoading(false);
        });
    }, SEARCH_SUGGESTIONS_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  function goToSearchResults(term) {
    const trimmed = term.trim();
    if (!trimmed) return;
    setSuggestionsOpen(false);
    navigate(`/products?search=${encodeURIComponent(trimmed)}`);
  }

  function goToProduct(productId) {
    setSuggestionsOpen(false);
    navigate(`/product/${productId}`);
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (activeSuggestionIndex >= 0 && suggestions[activeSuggestionIndex]) {
      goToProduct(suggestions[activeSuggestionIndex].productId);
      return;
    }
    goToSearchResults(query);
  }

  function handleSearchInputChange(e) {
    const value = e.target.value;
    setQuery(value);
    setSuggestionsOpen(value.trim().length >= SEARCH_SUGGESTIONS_MIN_LENGTH);
  }

  function handleSearchInputFocus() {
    if (query.trim().length >= SEARCH_SUGGESTIONS_MIN_LENGTH) {
      setSuggestionsOpen(true);
    }
  }

  function handleSearchKeyDown(e) {
    if (!suggestionsOpen || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveSuggestionIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveSuggestionIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
    } else if (e.key === "Escape") {
      setSuggestionsOpen(false);
    }
  }

  function closeAccountMenu() {
    setMenuOpen(false);
    setEditingName(false);
    setAccountError("");
  }

  function startEditingName() {
    setNameInput(user?.fullName || "");
    setAccountError("");
    setEditingName(true);
  }

  function cancelEditingName() {
    setEditingName(false);
    setNameInput("");
  }

  async function saveName() {
    const trimmed = nameInput.trim();
    if (trimmed.length < 2) {
      setAccountError(t("header:nameRequired"));
      return;
    }
    if (trimmed === (user?.fullName || "").trim()) {
      cancelEditingName();
      return;
    }
    setSavingName(true);
    setAccountError("");
    try {
      await updateFullName(trimmed);
      updateUser({ fullName: trimmed });
      setEditingName(false);
    } catch (err) {
      setAccountError(getErrorMessage(err, t("header:saveNameError")));
    } finally {
      setSavingName(false);
    }
  }

  async function handlePickImage(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    setUploadingImage(true);
    setAccountError("");
    try {
      const { profileImageUrl } = await uploadProfileImage(file);
      updateUser({ profileImageUrl });
    } catch (err) {
      setAccountError(getErrorMessage(err, t("header:updatePhotoError")));
    } finally {
      setUploadingImage(false);
    }
  }

  async function handleToggleMarketingOptIn(e) {
    const value = e.target.checked;
    if (savingMarketingOptIn) return;
    setSavingMarketingOptIn(true);
    setAccountError("");
    try {
      await updateMarketingOptIn(value);
      updateUser({ marketingOptIn: value });
    } catch (err) {
      setAccountError(getErrorMessage(err, t("header:updateMarketingOptInError")));
    } finally {
      setSavingMarketingOptIn(false);
    }
  }

  return (
    <header className="client-header" ref={headerRef}>
      <div className="client-header-inner">
        <Link to="/" className="client-header-brand">
          <img src="/apple-touch-icon.png" alt="" className="client-header-logo" />
          <span className="client-header-name">Salatty</span>
        </Link>

        <div className="client-header-search-wrap" ref={searchRef}>
          <form className="client-header-search" onSubmit={handleSubmit} autoComplete="off">
            <span className="client-header-search-icon">
              <SearchIcon />
            </span>
            <input
              type="search"
              placeholder={t("header:searchPlaceholder")}
              value={query}
              onChange={handleSearchInputChange}
              onFocus={handleSearchInputFocus}
              onKeyDown={handleSearchKeyDown}
              className="client-header-search-input"
              aria-label={t("header:searchAriaLabel")}
              role="combobox"
              aria-expanded={suggestionsOpen}
              aria-autocomplete="list"
              aria-controls="client-header-search-suggestions"
            />
            <button type="submit" className="client-header-search-button" aria-label={t("header:searchButtonAriaLabel")}>
              <SearchIcon />
            </button>
          </form>

          {suggestionsOpen && (
            <div className="client-header-suggestions" id="client-header-search-suggestions" role="listbox">
              {suggestionsLoading ? (
                <div className="client-header-suggestions-status">{t("common:loading")}</div>
              ) : suggestions.length === 0 ? (
                <div className="client-header-suggestions-status">{t("common:noResultsFound")}</div>
              ) : (
                <ul className="client-header-suggestions-list">
                  {suggestions.map((product, index) => {
                    const image = getMainImage(product);
                    return (
                      <li key={product.productId}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={index === activeSuggestionIndex}
                          className={`client-header-suggestion-item${index === activeSuggestionIndex ? " active" : ""}`}
                          onMouseEnter={() => setActiveSuggestionIndex(index)}
                          onClick={() => goToProduct(product.productId)}
                        >
                          {image ? (
                            <img src={image.imageUrl} alt="" className="client-header-suggestion-image" />
                          ) : (
                            <span className="client-header-suggestion-image client-header-suggestion-image-placeholder" />
                          )}
                          <span className="client-header-suggestion-name">{localizedProductName(language, product)}</span>
                          <span className="client-header-suggestion-price">
                            {(product.discountPrice ?? product.price).toFixed(2)} ₪
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              <button
                type="button"
                className="client-header-suggestions-viewall"
                onClick={() => goToSearchResults(query)}
              >
                {t("header:viewAllResultsFor", { search: query.trim() })}
              </button>
            </div>
          )}
        </div>

        <LanguageSwitcher />

        <div className="client-header-quicklinks">
        <Link to="/stores" className="client-header-stores-link">
          <StorefrontIcon />
          <span>{t("header:stores")}</span>
        </Link>

        {isAuthenticated && (
          <div className="client-header-icon-links">
            <Link to="/notifications" className="client-header-icon-link" aria-label={t("header:notificationsAriaLabel")}>
              <BellIcon />
              {notificationCount > 0 && (
                <span className="client-header-icon-badge">{notificationCount > 9 ? "9+" : notificationCount}</span>
              )}
            </Link>
            <Link to="/messages" className="client-header-icon-link" aria-label={t("header:messagesAriaLabel")}>
              <MessageIcon />
              {messageCount > 0 && (
                <span className="client-header-icon-badge">{messageCount > 9 ? "9+" : messageCount}</span>
              )}
            </Link>
            <Link to="/wishlist" className="client-header-icon-link" aria-label={t("header:wishlistAriaLabel")}>
              <HeartIcon />
              {wishlistCount > 0 && <span className="client-header-icon-badge">{wishlistCount}</span>}
            </Link>
            <Link to="/cart" className="client-header-icon-link" aria-label={t("header:cartAriaLabel")}>
              <CartIcon />
              {cartCount > 0 && <span className="client-header-icon-badge">{cartCount}</span>}
            </Link>
            <Link to="/orders" className="client-header-icon-link" aria-label={t("header:ordersAriaLabel")}>
              <ReceiptIcon />
            </Link>
          </div>
        )}
        </div>

        {isAuthenticated ? (
          <div className="client-header-account" ref={accountRef}>
            <button
              type="button"
              className="client-header-account-button"
              onClick={() => setMenuOpen((open) => !open)}
            >
              {user.profileImageUrl ? (
                <img src={user.profileImageUrl} alt="" className="client-header-account-avatar" />
              ) : (
                <UserIcon />
              )}
              <span className="client-header-account-name">{user.fullName?.split(" ")[0] || t("header:accountFallback")}</span>
            </button>
            {menuOpen && (
              <div className="client-header-account-menu">
                <div className="client-header-account-profile">
                  <button
                    type="button"
                    className="client-header-account-avatar-large-wrap"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    aria-label={t("header:changePhotoAriaLabel")}
                  >
                    {user.profileImageUrl ? (
                      <img src={user.profileImageUrl} alt="" className="client-header-account-avatar-large" />
                    ) : (
                      <span className="client-header-account-avatar-large-fallback">
                        {getInitials(user.fullName)}
                      </span>
                    )}
                    <span className="client-header-account-avatar-badge">
                      {uploadingImage ? (
                        <span className="client-header-spinner" />
                      ) : (
                        <CameraIcon />
                      )}
                    </span>
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="client-header-account-file-input"
                    onChange={handlePickImage}
                  />

                  {editingName ? (
                    <div className="client-header-account-name-edit">
                      <input
                        type="text"
                        className="client-header-account-name-input"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        maxLength={200}
                        disabled={savingName}
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") saveName();
                          if (e.key === "Escape") cancelEditingName();
                        }}
                      />
                      <button
                        type="button"
                        className="client-header-account-name-icon-btn"
                        onClick={saveName}
                        disabled={savingName}
                        aria-label={t("header:saveNameAriaLabel")}
                      >
                        <CheckIcon />
                      </button>
                      <button
                        type="button"
                        className="client-header-account-name-icon-btn"
                        onClick={cancelEditingName}
                        disabled={savingName}
                        aria-label={t("common:cancel")}
                      >
                        <CloseIcon />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="client-header-account-name-display"
                      onClick={startEditingName}
                    >
                      <span>{user.fullName || t("header:yourAccount")}</span>
                      <PencilIcon />
                    </button>
                  )}

                  {accountError && <p className="client-header-account-error">{accountError}</p>}
                </div>

                <div className="client-header-account-menu-divider" />

                <div className="client-header-account-section-label">{t("header:accountSectionLabel")}</div>

                <Link to="/addresses" className="client-header-account-row" onClick={closeAccountMenu}>
                  <span className="client-header-account-row-icon">
                    <MapPinIcon />
                  </span>
                  <span className="client-header-account-row-label">{t("header:myAddresses")}</span>
                  <ChevronRightIcon />
                </Link>

                <Link to="/wishlist" className="client-header-account-row" onClick={closeAccountMenu}>
                  <span className="client-header-account-row-icon">
                    <HeartIcon />
                  </span>
                  <span className="client-header-account-row-label">{t("header:wishlistAriaLabel")}</span>
                  {wishlistCount > 0 && <span className="client-header-account-row-count">{wishlistCount}</span>}
                  <ChevronRightIcon />
                </Link>

                <Link to="/followed-stores" className="client-header-account-row" onClick={closeAccountMenu}>
                  <span className="client-header-account-row-icon">
                    <StorefrontIcon />
                  </span>
                  <span className="client-header-account-row-label">{t("header:followedStores")}</span>
                  {followedStoresCount > 0 && (
                    <span className="client-header-account-row-count">{followedStoresCount}</span>
                  )}
                  <ChevronRightIcon />
                </Link>

                <Link to="/my-coupons" className="client-header-account-row" onClick={closeAccountMenu}>
                  <span className="client-header-account-row-icon">
                    <TagIcon />
                  </span>
                  <span className="client-header-account-row-label">{t("header:myCoupons")}</span>
                  <ChevronRightIcon />
                </Link>

                <Link to="/store-notifications" className="client-header-account-row" onClick={closeAccountMenu}>
                  <span className="client-header-account-row-icon">
                    <BellIcon />
                  </span>
                  <span className="client-header-account-row-label">{t("header:storeNotifications")}</span>
                  {bellStoresCount > 0 && <span className="client-header-account-row-count">{bellStoresCount}</span>}
                  <ChevronRightIcon />
                </Link>

                <div className="client-header-account-row client-header-account-row-toggle">
                  <span className="client-header-account-row-icon">
                    <MegaphoneIcon />
                  </span>
                  <span className="client-header-account-row-label">{t("header:marketingOptIn")}</span>
                  <label className="client-header-toggle-switch">
                    <input
                      type="checkbox"
                      checked={Boolean(user.marketingOptIn)}
                      disabled={savingMarketingOptIn}
                      onChange={handleToggleMarketingOptIn}
                      aria-label={t("header:marketingOptIn")}
                    />
                    <span className="client-header-toggle-switch-track" />
                  </label>
                </div>

                <div className="client-header-account-menu-divider" />

                <button
                  type="button"
                  className="client-header-account-menu-item danger"
                  onClick={() => {
                    closeAccountMenu();
                    logout();
                  }}
                >
                  {t("common:logOut")}
                </button>
              </div>
            )}
          </div>
        ) : (
          <button type="button" className="client-header-login-button" onClick={openLoginModal}>
            <UserIcon />
            {t("common:logIn")}
          </button>
        )}

        <Link to="/join" className="client-header-sell-link">
          {t("header:sellOnSalatty")}
        </Link>
      </div>

      {loginModalOpen && <ClientLoginModal onClose={closeLoginModal} />}
    </header>
  );
}
